import { useState } from 'react';
import { Check, Play, Trash2, Undo2 } from 'lucide-react';
import { useDeleteOrderTask, useSetOrderTaskState } from '@my/tms-api/hooks';
import type { TaskStateValue } from '@my/tms-api';
import { ConfirmDialog, cn, showToast } from '@my/ui';
import { RowActionsMenu, type RowAction } from '@/components/RowActionsMenu';

const STATE_LABEL: Record<TaskStateValue, string> = {
  Pending: 'Pending',
  InProgress: 'In progress',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
};

const STATE_TONE: Record<TaskStateValue, string> = {
  Pending: 'border-slate-300 bg-slate-50 text-slate-600',
  InProgress: 'border-amber-300 bg-amber-50 text-amber-800',
  Completed: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  Cancelled: 'border-red-300 bg-red-50 text-red-700',
};

/**
 * Where a leg has got to.
 *
 * <p>
 * Shown on every task, Pending included: the badge is the row's answer to "has
 * this happened yet", and a column that is blank for the commonest state makes
 * the reader check whether it is empty or just quiet. It is the only leg-level
 * progress there is, and the workflow already reads it — "all legs completed"
 * is what lets a trip finish.
 * </p>
 */
export function TaskStatePill({ state }: { state: TaskStateValue }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        STATE_TONE[state],
      )}
    >
      {STATE_LABEL[state]}
    </span>
  );
}

/** A leg that has finished — reopening one is a correction, not the normal flow. */
function isFinished(state: TaskStateValue) {
  return state === 'Completed' || state === 'Cancelled';
}

/**
 * The actions behind a leg's "⋮".
 *
 * <p>
 * Every state is reachable from every other, in both directions. The rows look
 * alike and this menu sits in the same place on each, so starting or cancelling
 * the wrong leg is an ordinary mis-click — and leaving an operator with a wrong
 * record and no way to fix it is worse for the history than the correction is.
 * What keeps a reversal honest is the audit trail, which names who reopened what
 * and when, rather than a locked door.
 * </p>
 *
 * <p>
 * Reopening something FINISHED asks first, and says that the recorded times stay
 * as they are: there is no way to tell a time the server stamped on completion
 * from one somebody typed, so clearing them would risk erasing what actually
 * happened. The task form is where a wrong time is corrected.
 * </p>
 *
 * <p>
 * The three state moves are always listed, disabled only for the state the
 * task is already in — the same bargain the orders list makes with this
 * component, so the menu reads the same shape every time and an operator
 * learns it.
 * </p>
 *
 * <p>
 * A leg that should not happen is DELETED, not cancelled. Cancelling left the
 * row on the order marked red, which reads as "still here, still to be dealt
 * with" on every screen that lists legs — and a leg nobody is going to run has
 * no business in the plan, the manifest or the journey. The row is soft
 * deleted, so it is gone from the product but recoverable in the database, and
 * the audit trail names who removed it.
 * </p>
 */
export function TaskStateMenu({
  orderId,
  taskId,
  taskLabel,
  state,
}: {
  orderId: string;
  taskId: string;
  /** Names the task in the button's accessible name and the confirm dialog. */
  taskLabel: string;
  state: TaskStateValue;
}) {
  const mutation = useSetOrderTaskState();
  const deleteMutation = useDeleteOrderTask();
  const [confirming, setConfirming] = useState<TaskStateValue | null>(null);
  const [deleting, setDeleting] = useState(false);

  const move = async (to: TaskStateValue) => {
    try {
      await mutation.mutateAsync({ orderId, taskId, state: to });
      showToast({ type: 'success', message: `Task marked ${STATE_LABEL[to]}.` });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Could not change the task status.',
      });
    }
  };

  const remove = async () => {
    try {
      await deleteMutation.mutateAsync({ orderId, taskId });
      showToast({ type: 'success', message: 'Task deleted.' });
    } catch (err) {
      // The BE refuses a booking half on its own — both halves go together.
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Could not delete the task.',
      });
    }
  };

  /** Reopening a finished leg asks first; an ordinary forward move just goes. */
  const select = (to: TaskStateValue) => {
    if (isFinished(state)) setConfirming(to);
    else void move(to);
  };

  const item = (to: TaskStateValue, label: string, icon: RowAction['icon']): RowAction => ({
    id: to,
    label,
    icon,
    onSelect: () => select(to),
    disabledReason:
      state === to
        ? `Already ${STATE_LABEL[to].toLowerCase()}.`
        : mutation.isPending
          ? 'Saving…'
          : undefined,
  });

  const actions: RowAction[] = [
    item('InProgress', 'Mark as in progress', <Play className="h-3.5 w-3.5 text-sky-600" />),
    // Straight from Pending as well as from In progress: a driver rings in after
    // the fact more often than anyone updates a screen mid-run.
    item('Completed', 'Mark as completed', <Check className="h-3.5 w-3.5 text-emerald-600" />),
    item('Pending', 'Move back to pending', <Undo2 className="h-3.5 w-3.5 text-slate-500" />),
    {
      id: 'delete',
      label: 'Delete task',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      onSelect: () => setDeleting(true),
      disabledReason: deleteMutation.isPending ? 'Deleting…' : undefined,
      destructive: true,
    },
  ];

  return (
    <>
      <RowActionsMenu label={taskLabel} actions={actions} />

      <ConfirmDialog
        open={confirming !== null}
        title="Reopen this task?"
        message={`This task is ${STATE_LABEL[state].toLowerCase()}. Reopening it moves it back to ${
          confirming ? STATE_LABEL[confirming].toLowerCase() : ''
        } and is recorded in the history. Any times already recorded against it stay as they are — correct them in the task if they are wrong.`}
        confirmLabel="Reopen task"
        busy={mutation.isPending}
        onConfirm={async () => {
          const to = confirming;
          setConfirming(null);
          if (to) await move(to);
        }}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={deleting}
        tone="danger"
        title="Delete this task?"
        message={
          // A completed leg has times recorded against it. Worth naming in the
          // dialog, not worth refusing: the operator can see the state and is
          // the one who knows whether the leg should be there at all.
          state === 'Completed'
            ? `${taskLabel} is marked completed and has times recorded against it. Deleting it removes the leg and those times from the order. This cannot be undone.`
            : `${taskLabel} will be removed from the order. This cannot be undone.`
        }
        confirmLabel="Delete task"
        busy={deleteMutation.isPending}
        onConfirm={async () => {
          setDeleting(false);
          await remove();
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}
